import { useCallback, useEffect, useRef } from 'react';

/**
 * Calls `onRefresh` once per delay when `schedule` is called, so data a finished run wrote is
 * re-read even if it commits a moment after the run's terminal event. `schedule` restarts the
 * series and `cancel` stops it; both are stable, and the series stops on unmount.
 */
export const useDelayedRefresh = (
  onRefresh: (() => void) | undefined,
  delaysMs: number[]
): { schedule: () => void; cancel: () => void } => {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const delaysRef = useRef(delaysMs);
  delaysRef.current = delaysMs;
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  const cancel = useCallback(() => {
    timeouts.current.forEach(clearTimeout);
    timeouts.current = [];
  }, []);

  const schedule = useCallback(() => {
    cancel();
    const refresh = () => onRefreshRef.current?.();
    // A zero delay runs now, so the first re-read is not a tick behind the event that triggered it.
    if (delaysRef.current.includes(0)) refresh();
    timeouts.current = delaysRef.current.filter((delay) => delay > 0).map((delay) => setTimeout(refresh, delay));
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  return { schedule, cancel };
};

export default useDelayedRefresh;
